import { useCallback, useMemo } from "react";
import { useACLContext } from "../context";
import type {
  PermissionAction,
  ResourceType,
  EvaluationResult,
} from "@shield-acl/core";

/**
 * Hook que fornece acesso direto ao método evaluate com memoização
 * Útil quando você precisa não apenas do resultado booleano, mas também dos detalhes da avaliação
 *
 * @returns Função evaluate memoizada
 *
 * @example
 * ```tsx
 * const evaluate = useEvaluate()
 *
 * // Avaliar com detalhes
 * const result = evaluate("posts.edit", "posts", { postId: 123 })
 *
 * if (!result.allowed) {
 *   console.log(`Acesso negado: ${result.reason}`)
 *
 *   if (result.matchedRule?.deny) {
 *     alert("Você foi explicitamente negado desta ação")
 *   }
 * }
 *
 * // Em um formulário com feedback detalhado
 * function PermissionFeedback({ action, resource }) {
 *   const evaluate = useEvaluate()
 *   const result = evaluate(action, resource)
 *
 *   return (
 *     <div className={result.allowed ? "text-green" : "text-red"}>
 *       {result.allowed ? "✓" : "✗"} {action}
 *       {!result.allowed && (
 *         <small>{result.reason}</small>
 *       )}
 *     </div>
 *   )
 * }
 * ```
 */
export function useEvaluate<TContext = any>() {
  const { evaluate } = useACLContext<TContext>();
  return evaluate;
}

/**
 * Hook que avalia uma permissão específica e retorna o resultado memoizado
 * Similar ao useCan, mas retorna o objeto EvaluationResult completo
 *
 * @param action - Ação a avaliar
 * @param resource - Recurso opcional
 * @param context - Contexto adicional opcional
 * @returns Resultado completo da avaliação
 *
 * @example
 * ```tsx
 * const editResult = useEvaluationResult("posts.edit", "posts", { postId: 123 })
 *
 * if (!editResult.allowed) {
 *   return (
 *     <Tooltip content={editResult.reason}>
 *       <Button disabled>
 *         Editar (sem permissão)
 *       </Button>
 *     </Tooltip>
 *   )
 * }
 * ```
 */
export function useEvaluationResult<TContext = any>(
  action: PermissionAction,
  resource?: ResourceType,
  context?: TContext,
): EvaluationResult {
  const { evaluate, user } = useACLContext<TContext>();

  return useMemo(() => {
    return evaluate(action, resource, context);
  }, [evaluate, action, resource, context, user]);
}

/**
 * Hook que fornece funções utilitárias para debug e análise de permissões
 *
 * @returns Objeto com funções de análise
 *
 * @example
 * ```tsx
 * const { explainDenial, findMatchingRule, testPermission } = usePermissionAnalysis()
 *
 * // Explicar por que uma permissão foi negada
 * const explanation = explainDenial("admin.delete", "users")
 * console.log(explanation)
 * // "Negado: No matching permission found (default deny)"
 *
 * // Encontrar qual regra está sendo aplicada
 * const rule = findMatchingRule("posts.edit", "posts")
 * if (rule) {
 *   console.log(`Matched rule: ${rule.action} on ${rule.resource}`)
 * }
 *
 * // Testar permissão com diferentes contextos
 * const scenarios = testPermission("posts.edit", "posts", [
 *   { postId: 1, authorId: 123 },
 *   { postId: 2, authorId: 456 },
 *   { postId: 3, authorId: 789 }
 * ])
 * ```
 */
export function usePermissionAnalysis<TContext = any>() {
  const { evaluate, user } = useACLContext<TContext>();

  /**
   * Explica por que uma permissão foi negada
   */
  const explainDenial = useCallback(
    (
      action: PermissionAction,
      resource?: ResourceType,
      context?: TContext,
    ): string => {
      const result = evaluate(action, resource, context);
      if (result.allowed) {
        return "Permissão concedida";
      }
      return result.reason || "Permissão negada por razão desconhecida";
    },
    [evaluate],
  );

  /**
   * Encontra a regra que está sendo aplicada para uma permissão
   */
  const findMatchingRule = useCallback(
    (action: PermissionAction, resource?: ResourceType, context?: TContext) => {
      const result = evaluate(action, resource, context);
      return result.matchedRule || null;
    },
    [evaluate],
  );

  /**
   * Testa uma permissão com múltiplos contextos
   */
  const testPermission = useCallback(
    (
      action: PermissionAction,
      resource?: ResourceType,
      contexts?: TContext[],
    ) => {
      if (!contexts || contexts.length === 0) {
        const result = evaluate(action, resource);
        return [{ context: undefined, ...result }];
      }

      return contexts.map((context) => ({
        context,
        ...evaluate(action, resource, context),
      }));
    },
    [evaluate],
  );

  return useMemo(
    () => ({
      explainDenial,
      findMatchingRule,
      testPermission,
    }),
    [explainDenial, findMatchingRule, testPermission, user],
  );
}
